package com.essntl.features.itinerary.ui.edit_itinerary.components

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import core.dateformat.utils.parseLocalDateOrNull
import com.essntl.features.itinerary.domain.model.ItineraryModel
import com.essntl.features.itinerary.ui.edit_itinerary.EditItineraryEvent
import com.essntl.features.itinerary.ui.edit_itinerary.EditItineraryState
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.dialog.EsDialog
import com.essntl.uikit.components.input.date.EsDateInput
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberNumberOfDays
import core.validation.EsInputState

@Composable
fun ItineraryDatesDialog(
    state: EditItineraryState,
    onEvent: (EditItineraryEvent) -> Unit,
    itinerary: ItineraryModel,
    onDismissRequest: () -> Unit,
) {
    var proposal by remember { mutableStateOf(itinerary.proposal ?: ProposalModel.empty) }

    var fromDateInputState by remember { mutableStateOf(EsInputState.Default) }
    var toDateInputState by remember { mutableStateOf(EsInputState.Default) }
    var fromDateInputMessage by remember { mutableStateOf("This field is required") }
    var toDateInputMessage by remember { mutableStateOf("This field is required") }

    val dateDiff = rememberNumberOfDays(
        proposal.fromDate,
        proposal.toDate,
    )

    LaunchedEffect(state.editProposal.isSuccess) {
        if (state.editProposal.isSuccess) {
            onDismissRequest()
        }
    }

    LaunchedEffect(dateDiff) {
        if (dateDiff != null && dateDiff < 0) {
            toDateInputMessage = "To date must be after from date"
            toDateInputState = EsInputState.Error
        }
    }

    fun validateFromDate(fromDate: String? = proposal.fromDate) =
        if (fromDate.isNullOrBlank()) {
            fromDateInputMessage = "This field is required"
            fromDateInputState = EsInputState.Error
            false
        } else {
            val fromLocalDate = parseLocalDateOrNull(fromDate)
            if (fromLocalDate == null) {
                fromDateInputMessage = "Please enter a valid date"
                fromDateInputState = EsInputState.Error
                false
            } else {
                fromDateInputMessage = "This field is required"
                fromDateInputState = EsInputState.Default
                true
            }
        }

    fun validateToDate(toDate: String? = proposal.toDate) =
        if (toDate.isNullOrEmpty()) {
            toDateInputMessage = "This field is required"
            toDateInputState = EsInputState.Error
            false
        } else {
            val toLocalDate = parseLocalDateOrNull(toDate)
            if (toLocalDate == null) {
                toDateInputMessage = "Please enter a valid date"
                toDateInputState = EsInputState.Error
                false
            } else if (dateDiff != null && dateDiff < 0) {
                toDateInputMessage = "To date must be after from date"
                toDateInputState = EsInputState.Error
                false
            } else {
                toDateInputMessage = "This field is required"
                toDateInputState = EsInputState.Default
                true
            }
        }

    fun validate(): Boolean {
        val validationFields =
            listOf(
                validateFromDate(),
                validateToDate(),
            )
        return validationFields.none { !it }
    }

    EsDialog(
        title = "Dates",
        onDismissRequest = onDismissRequest,
    ) {
        Column(
            modifier =
            Modifier
                .fillMaxWidth()
                .padding(
                    horizontal = 2.5f.spacing,
                )
                .padding(
                    top = 2.spacing,
                    bottom = 2.5f.spacing,
                ),
        ) {
            EsDateInput(
                value = proposal.fromDate.orEmpty(),
                onValueChange = {
                    validateFromDate(it)
                    proposal =
                        proposal.copy(
                            fromDate = (it),
                        )
                },
                label = "From",
                state = fromDateInputState,
                message = fromDateInputMessage,
            )

            Spacer(modifier = Modifier.height(2.spacing))

            EsDateInput(
                value = proposal.toDate.orEmpty(),
                onValueChange = {
                    validateToDate(it)
                    proposal =
                        proposal.copy(
                            toDate = (it),
                        )
                },
                label = "To",
                state = toDateInputState,
                message = toDateInputMessage,
            )

            Spacer(modifier = Modifier.height(2.spacing))

            EsLargePrimaryButton(
                onClick = {
                    if (validate())
                        onEvent(EditItineraryEvent.EditItinerary(proposal))
                },
                text = "Save changes",
                loading = state.editProposal.isLoading,
                enabled = !state.editProposal.isLoading,
                modifier = Modifier
                    .fillMaxWidth(),
            )
        }
    }
}
